import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getVehicle } from '../services/api';

export const VehicleDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadVehicle = async () => {
      try {
        const response = await getVehicle(id);
        setVehicle(response.data);
      } catch (error) {
        console.error('Error loading vehicle:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadVehicle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl">Loading vehicle details...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Vehicle not found</h1>
          <Link to="/inventory">
            <Button>Back to Inventory</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/inventory">
            <Button variant="ghost" className="text-teal-600 hover:text-teal-700">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Inventory
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="aspect-video rounded-lg overflow-hidden mb-4 shadow-lg">
              <img 
                src={vehicle.images[currentImageIndex]} 
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {vehicle.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    currentImageIndex === index ? 'border-teal-600 shadow-md' : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img 
                    src={image} 
                    alt={`View ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle Info */}
          <div>
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h1>
                  <span className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                    {vehicle.condition}
                  </span>
                </div>
              </div>
              <div className="text-4xl font-bold text-teal-600 mb-6">
                ${vehicle.price.toLocaleString()}
              </div>
            </div>

            {/* Key Specs */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Key Specifications</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500 text-sm">Mileage</p>
                    <p className="font-semibold text-lg">{vehicle.mileage.toLocaleString()} miles</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Transmission</p>
                    <p className="font-semibold text-lg">{vehicle.transmission}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Fuel Type</p>
                    <p className="font-semibold text-lg">{vehicle.fuelType}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Year</p>
                    <p className="font-semibold text-lg">{vehicle.year}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Exterior</p>
                    <p className="font-semibold text-lg">{vehicle.exteriorColor}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Interior</p>
                    <p className="font-semibold text-lg">{vehicle.interiorColor}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3">Description</h2>
                <p className="text-gray-700">{vehicle.description}</p>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Features</h2>
                <div className="grid grid-cols-1 gap-2">
                  {vehicle.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Check className="h-5 w-5 text-teal-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Contact CTA */}
            <Card className="bg-teal-600 text-white">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Interested in this vehicle?</h2>
                <p className="mb-6">Contact us today to schedule a test drive or get more information</p>
                <div className="space-y-3">
                  <a href="tel:4047231672">
                    <Button size="lg" className="w-full bg-white text-teal-600 hover:bg-gray-100">
                      <Phone className="mr-2 h-5 w-5" />
                      Call (404) 723-1672
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full border-2 border-white text-white hover:bg-teal-700">
                      <Mail className="mr-2 h-5 w-5" />
                      Send Message
                    </Button>
                  </Link>
                  <a href="https://www.google.com/maps/search/?api=1&query=2759+DELK+RD+SE+MARIETTA+GA+30067" target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full border-2 border-white text-white hover:bg-teal-700">
                      <MapPin className="mr-2 h-5 w-5" />
                      Get Directions
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
